import { useLocalStorage } from "@/persist";
import type { AiChatMessage, AiChatMessageMeta } from "@/types/ai-chat";
import { t } from "@lingui/core/macro";
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const AI_CHAT_SESSION_KEY = "aiChat.session";
export const AI_CHATBOT_NAME = "MetX Assistant";

type StoredConversation = {
  conversationId: string | null;
  messages: AiChatMessage[];
};

const cloneMessages = (messages: AiChatMessage[]): AiChatMessage[] => messages.map((msg) => ({ ...msg }));

const createDefaultSession = (): StoredConversation => {
  const baseSystemMessage: AiChatMessage = {
    id: "sys-0",
    role: "system",
    content: t`Hello, I'm the Meteomatics ${AI_CHATBOT_NAME}. How can I help you?`,
  };

  return {
    conversationId: null,
    messages: cloneMessages([baseSystemMessage]),
  };
};

const isValidMessage = (value: unknown): value is AiChatMessage =>
  !!value && typeof value === "object" && typeof (value as AiChatMessage).id === "string";

const isValidSession = (value: unknown): value is StoredConversation =>
  !!value &&
  typeof value === "object" &&
  Array.isArray((value as StoredConversation).messages) &&
  (value as StoredConversation).messages.every(isValidMessage) &&
  "conversationId" in (value as StoredConversation);

export interface UseAiChatSessionResult {
  messages: AiChatMessage[];
  setMessages: Dispatch<SetStateAction<AiChatMessage[]>>;
  conversationIdState: string | null;
  setConversationIdState: Dispatch<SetStateAction<string | null>>;
  processedActionIdsRef: MutableRefObject<Set<string>>;
  applyMessageMeta: (assistantId: string, messageMeta: AiChatMessageMeta, userId?: string) => void;
  markActionsProcessed: (assistantId: string) => void;
  resetSession: () => void;
}

export function useAiChatSession(): UseAiChatSessionResult {
  const [persistedSession, setPersistedSession] = useLocalStorage<StoredConversation>(
    AI_CHAT_SESSION_KEY,
    isValidSession,
    createDefaultSession(),
    "persist-by-default",
  );

  const initialSessionRef = useRef<StoredConversation>(
    persistedSession?.messages.length ? persistedSession : createDefaultSession(),
  );

  const [conversationIdState, setConversationIdState] = useState<string | null>(
    initialSessionRef.current.conversationId,
  );
  const [messages, setMessages] = useState<AiChatMessage[]>(initialSessionRef.current.messages);
  const processedActionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    setPersistedSession(
      {
        conversationId: conversationIdState ?? null,
        messages,
      },
      true,
    );
    try {
      window.dispatchEvent(
        new CustomEvent("aiChatSessionUpdated", {
          detail: { conversationId: conversationIdState ?? null, messages },
        }),
      );
    } catch {
      // Ignore dispatch errors (e.g., SSR)
    }
  }, [conversationIdState, messages, setPersistedSession]);

  useEffect(() => {
    const processed = new Set<string>();
    for (const msg of initialSessionRef.current.messages) {
      if (msg.role === "assistant" && (msg.meta as any)?.actions_processed) {
        processed.add(msg.id);
      }
    }
    if (processed.size > 0) {
      processedActionIdsRef.current = processed;
    }
  }, []); // run once on mount

  const applyMessageMeta = useCallback((assistantId: string, messageMeta: AiChatMessageMeta, userId?: string) => {
    setMessages((prev) => {
      const next = [...prev];
      for (let i = 0; i < next.length; i += 1) {
        const msg = next[i];
        if (msg.id === assistantId) {
          next[i] = { ...msg, meta: { ...msg.meta, ...messageMeta } };
        }
        if (userId && messageMeta.transcript && msg.id === userId) {
          next[i] = { ...msg, content: messageMeta.transcript };
        }
      }
      return next;
    });
  }, []);

  const markActionsProcessed = useCallback((assistantId: string) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === assistantId ? { ...msg, meta: { ...msg.meta, actions_processed: true } } : msg)),
    );
  }, []);

  const resetSession = useCallback(() => {
    const nextSession = createDefaultSession();
    setMessages(nextSession.messages);
    setConversationIdState(nextSession.conversationId);
    processedActionIdsRef.current = new Set();
    setPersistedSession(nextSession, true);
  }, [setPersistedSession]);

  return {
    messages,
    setMessages,
    conversationIdState,
    setConversationIdState,
    processedActionIdsRef,
    applyMessageMeta,
    markActionsProcessed,
    resetSession,
  };
}
